//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useState } from "react";
import { Col, Button, Container, Card, Row, Table, Form, Pagination } from "react-bootstrap";
import { useTable, useFilters, useGlobalFilter, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";
import { Context } from "../../common/context/Context";
import { Link } from 'react-router-dom';
import { AddManageGAActions } from "../addManageGAActions";
import ErrorAlertMessage from "../../common/alertMessages/errorAlertMessage";

const ViewGAContent = ({ title }) => {
    return (
        <div>
            <h3>{title} </h3>
        </div>
    )
};

const CardWithButton = ({ genericAccountCMSData }) => {

    const navigate = useNavigate();

    const fetchViewGenericAccountLabel = (key) => {
        if (genericAccountCMSData && genericAccountCMSData.Labels) {
            return genericAccountCMSData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }

    return (
        <Card>
            <Card.Body>
                <Card.Text>
                    {fetchViewGenericAccountLabel("viewGenericAccounts.CardDescription")}
                </Card.Text>
                <Button
                    href="#"
                    variant="primary"
                    onClick={() => { navigate("/add-manage-generic-accounts") }}
                >
                    {fetchViewGenericAccountLabel("viewGenericAccounts.CardButton")}
                </Button>
            </Card.Body>
        </Card>
    )
};

function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}) {
    const options = React.useMemo(() => {
        const options = new Set();
        preFilteredRows.forEach((row) => {
            options.add(row.values[id]);
        });
        return [...options.values()];
    }, [id, preFilteredRows]);

    return (
        <Form style={{ display: "flex" }}>
            <Form.Select
                id={id}
                aria-label="select filter"
                value={filterValue}
                onChange={(e) => {
                    (e.target.value != "All") ? setFilter(e.target.value || undefined) : setFilter(undefined);
                }}
            >
                <option value="All">All</option>
                {options.map((option, i) => (
                    <option key={i} value={option}>
                        {option}
                    </option>
                ))}
            </Form.Select>
        </Form>
    );
}

function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter, id },
}) {
    const count = preFilteredRows.length;

    const clearFilter = () => {
        setFilter(undefined);
    }

    return (
        <Form style={{ display: "flex" }}>
            <Form.Group>
                <Form.Control
                    id={id}
                    value={filterValue || ""}
                    onChange={(e) => {
                        setFilter(e.target.value || undefined);
                    }}
                    placeholder={`Search ${count} records...`}
                    className="mt-2"
                />
            </Form.Group>
            <Button
                onClick={(e) => clearFilter(e)}
                variant="secondary"
                className="close btn-sm"
                style={{ height: "100%", alignSelf: "end", marginLeft: "1px", padding: "0 0.25rem" }}
            >
                X
            </Button>
        </Form>
    );
}

const ColumnFilteringTable = ({ columns, data, cmsData }) => {

    const filterTypes = React.useMemo(
        () => ({
            // Or, override the default text filter to use
            // "startWith"
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const fetchCommonComponentLabel = (key) => {
        if (cmsData && cmsData.Labels) {
            return cmsData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }


    // let cardData = cmsData && cmsData;
    // const fetchAddCardLabels = (key) => {
    //     let refCmsContent = cardData && cardData?.Properties;
    //     let label = "";
    //     if (refCmsContent?.Labels) {
    //         refCmsContent?.Labels.map((x) => {
    //             if (x.Key === key) {
    //                 label = x.Phrase;
    //             }
    //         });
    //     }
    //     return label;
    //  }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
            initialState: { pageIndex: 0 },
        },
        useFilters, // useFilters!
        useGlobalFilter,// useGlobalFilter!
        usePagination
    );



    const returnPageIndexText = (currentPage, TotalPage) => {
        const pageText = fetchCommonComponentLabel("Common.Page");
        let replaceText = pageText.replace('Page', '<span>Page</span>').replace('#Currentpage#', '<strong>' + currentPage + '</strong>').replace('#Totalpage#', '<strong>' + TotalPage + '</strong>');
        return (
            <div className="me-2 mt-1" dangerouslySetInnerHTML={{ __html: replaceText }} />
        )
    }

    return (
        <Card>
            <Card.Body>
                <Table striped bordered {...getTableProps()} responsive>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps()}>
                                        {column.render("Header")}
                                        {/* Render the columns filter UI */}
                                        <div>
                                            {column.canFilter ? column.render("Filter") : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <Row className="mt-3">
                    <Col md="6" className="d-flex flex-lg-row flex-column">
                        {returnPageIndexText(pageIndex + 1, pageOptions.length)}
                        {/* <div className="me-2 mt-1">
                            <span>Page </span>
                            <strong>
                                {pageIndex + 1} {fetchCommonComponentLabel("Common.Of")} {pageOptions.length}
                            </strong>
                        </div> */}
                        <div className="me-2">
                            <span className="me-2">{fetchCommonComponentLabel("Common.Show")}</span>
                            <Form.Select
                                className="d-inline-block w-auto"
                                value={pageSize}
                                aria-label="pageNumber"
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="me-2">
                            <span className="me-2">{fetchCommonComponentLabel("Common.GoToPage")}</span>
                            <Form.Control
                                className="d-inline-block"
                                type="number"
                                aria-label="number"
                                min="1"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                style={{ width: "75px" }}
                            />
                        </div>
                    </Col>
                    <Col md="6" className="mt-1">
                        <Pagination className="float-lg-end float-md-end float-left">
                            <Pagination.First
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Prev
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Next
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                            />
                            <Pagination.Last
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            />
                        </Pagination>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

const ViewGA = ({ candidateCMSData }) => {
    const context = useContext(Context);
    const [addManageGAJson, setAddManageGAJson] = useState({});
    const [errorAlert, setErrorAlert] = useState({
        showAlert: false
    })
    const [genericAccountCMSData, setGenericAccountCMSData] = useState({});
    const [commonComponentCMSData, setCommonComponentCMSData] = useState({});

    // const fetchAddManageGAJson = async () => {

    //     let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    //     await AddManageGAActions.GetAddManageGAJSON().then(
    //         (res) => {
    //             if (res.status === 200 || res.status === 204) {
    //                 setAddManageGAJson(res.data && res.data);
    //             }
    //             else {
    //                 refErrorMessageAlert.showAlert = true;
    //                 setErrorAlert(refErrorMessageAlert);
    //                 window.scrollTo(0, 0);
    //             }
    //         },
    //         (err) => {
    //             console.log(err);
    //         }
    //     );
    // }

    const fetchViewGenericAccountLabel = (key) => {
        if (genericAccountCMSData && genericAccountCMSData.Labels) {
            return genericAccountCMSData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }

    // const fetchAddManageGAlabels = (key) => {
    //     let refCmsContent = addManageGAJson && addManageGAJson?.Properties;
    //     let label = "";
    //     if (refCmsContent?.Labels) {
    //         refCmsContent?.Labels.map((x) => {
    //             if (x.Key === key) {
    //                 label = x.Phrase;
    //             }
    //         });
    //     }
    //     return label;
    // }

    const tableColumns = [
        {
            id: "viewGenericAccounts.GenericAccountsHeader",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.GenericAccountsHeader"),
            accessor: "accountName",
        },
        {
            id: "viewGenericAccounts.RegistrationCode",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.RegistrationCode"),
            accessor: "referenceCode",
            Cell: ({ row, value }) =>
            (context?.permissionsList && context?.permissionsList.includes("Generic Account.viewgenericaccount") ? <Link to={'/generic-accounts-details'}
                state={{ viewRefCode: value, isFromViewGa: true }}>
                {value}
            </Link> : value)
        },
        {
            id: "viewGenericAccounts.NoOfAccountsRegistered",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.NoOfAccountsRegistered"),
            accessor: "numberOfAccountsAllowed",
            Filter: false,
        },
        {
            id: "viewGenericAccounts.NoOfRegisteredUsingCode",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.NoOfRegisteredUsingCode"),
            accessor: "currentCount",
            Filter: false,
        },
        {
            id: "viewGenericAccounts.Active",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.Active"),
            accessor: (values) => {
                const { isActive } = values;
                return isActive ? "Yes" : "No"
            },
            Filter: SelectColumnFilter,
        },
        {
            id: "viewGenericAccounts.ExpirationDate",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.ExpirationDate"),
            accessor: "endDate",
        },
        {
            id: "viewGenericAccounts.RequestedBy",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.RequestedBy"),
            accessor: "requestedBy",
            Filter: SelectColumnFilter,
        },
        {
            id: "viewGenericAccounts.Country",
            Header: fetchViewGenericAccountLabel("viewGenericAccounts.Country"),
            accessor: "country",
            Filter: SelectColumnFilter,
        },
    ];

    const showErrorModal = () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        refErrorMessageAlert.showAlert = false;
        setErrorAlert(refErrorMessageAlert)
    }

    useEffect(() => {
        window.scrollTo(0, 0)
        //  fetchAddManageGAJson()
    }, [])

    useEffect(() => {
        setGenericAccountCMSData(candidateCMSData?.pageContent["viewGenericAccounts.GenericAccount"]);
        setCommonComponentCMSData(candidateCMSData?.pageContent["CommonComponent"]);
    }, [candidateCMSData])

    return (
        <React.Fragment>
            <Col lg='12'>
                <Container fluid className="p-0">
                    <Row>
                        <Col md="6" lg="4" className="mt-3">
                            <ViewGAContent title={genericAccountCMSData?.Title} />
                        </Col>
                    </Row>
                </Container>

                {errorAlert?.showAlert &&
                    <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
                }

                {context?.permissionsList && context?.permissionsList.includes("Generic Account.creategenericaccount") &&

                    <Container fluid className="p-0 mt-3">
                        <Row>
                            <Col md="6" lg="12">
                                <CardWithButton genericAccountCMSData={genericAccountCMSData && genericAccountCMSData} />
                            </Col>
                        </Row>
                    </Container>
                }

                <Container fluid className="p-0">
                    <h1 className="h3 mb-3">{fetchViewGenericAccountLabel("viewGenericAccounts.TableName")} </h1>
                    {context.viewGATableData ?
                        <ColumnFilteringTable
                            columns={tableColumns && tableColumns}
                            data={context.viewGATableData && context.viewGATableData}
                            cmsData={commonComponentCMSData && commonComponentCMSData}
                        />
                        : fetchViewGenericAccountLabel("viewGenericAccounts.NoTableData")
                    }
                </Container>
            </Col>
        </React.Fragment>
    )
}

export default ViewGA;
